<template>
  <LazyHydrate v-bind="hydrationStrategy" @hydrated="onHydration">
    <StandardWrapper
      v-if="context.company && context.company.location"
      v-bind="$attrs"
      :blok="blok"
      width="full"
      no-padding
    >
      <div
        class="jb-location-map-with-address flex flex-col md:flex-row"
        :class="[coordinates ? 'md:h-104' : '']"
      >
        <div
          class="flex flex-col justify-center px-8 py-12 md:order-2 md:px-12 md:py-16"
          :class="[
            coordinates ? 'md:order-2 md:w-2/5 xl:w-1/3' : 'w-full',
            backgroundColorClass(blok.bgVariant),
          ]"
        >
          <BasicAddress
            :class="textColorClass(blok.textVariant, blok.bgVariant)"
            :title="context.company.name"
            :address="context.company.location"
            :phone="context.company.phone"
          />

          <div v-if="hasAddress && blok.showRouteLink" class="mt-8 ml-2">
            <a
              :href="routeLink"
              target="_blank"
              class="link font-medium underline"
              :class="textColorClass(blok.textVariant, blok.bgVariant)"
              >{{ routeLinkText }}</a
            >
          </div>
        </div>

        <div
          class="relative h-72 w-full overflow-hidden md:order-1 md:h-auto md:w-3/5 xl:w-2/3"
        >
          <div
            :id="`mapContainer-${blok._uid}`"
            lang="en"
            class="h-full w-full"
          />
        </div>
      </div>
    </StandardWrapper>
  </LazyHydrate>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import BasicAddress from '../components/BasicAddress.vue'
import StandardWrapper from '../components/StandardWrapper.vue'
import { useColorPalette } from '../composables/colorPalette'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})

const { backgroundColorClass, textColorClass, colorCustomClass } =
  useColorPalette()

const map = ref(null)

const hydrationStrategy = computed(() => {
  return {
    whenVisible: { rootMargin: '400px' },
  }
})
const coordinates = computed(() => {
  if (props.context.company?.location?.geo) {
    return [
      props.context.company.location.geo.lng,
      props.context.company.location.geo.lat,
    ].map(Number)
  }
  return null
})
const markerColor = computed(() => {
  return props.blok.markerColor ? props.blok.markerColor : 'default'
})
const zoomLevel = computed(() => {
  if (
    props.blok.initialZoomLevel &&
    props.blok.initialZoomLevel > 0 &&
    props.blok.initialZoomLevel <= 18
  ) {
    return props.blok.initialZoomLevel
  }
  return 10
})
const hasAddress = computed(() => {
  return !!props.context.company?.location?.geo
})
const routeLinkText = computed(() => {
  return props.blok.routeLinkText || 'Show me how to get there'
})
const routeLink = computed(() => {
  const coords = props.context.company.location.geo
  return `https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lng}`
})

function onHydration() {
  makeMap()
}
function makeMap() {
  if (!coordinates.value || map.value?._removed) {
    return
  }

  map.value = new maplibregl.Map({
    container: `mapContainer-${props.blok._uid}`,
    style: `https://tiles.locationiq.com/v3/streets/raster.json?key=${
      import.meta.env.VITE_LOCATION_IQ_TOKEN
    }`,
    center: coordinates.value,
    maxZoom: 18,
    zoom: zoomLevel.value,
    pitchWithRotate: false,
    touchPitch: false,
    cooperativeGestures: true,
    locale: {
      'CooperativeGesturesHandler.WindowsHelpText':
        'Gebruik Ctrl + scroll om in te zoomen op de kaart',
      'CooperativeGesturesHandler.MacHelpText':
        'Gebruik ⌘ + scroll om in te zoomen op de kaart',
      'CooperativeGesturesHandler.MobileHelpText':
        'Gebruik twee vingers om de kaart te verplaatsen',
    },
  })

  // Add zoom controls to the map.
  map.value.addControl(
    new maplibregl.NavigationControl({
      showCompass: false,
    }),
    'top-left',
  )

  // Add the marker
  new maplibregl.Marker({
    color: `var(--${colorCustomClass(markerColor.value, null, '')})`,
  })
    .setLngLat(coordinates.value)
    .addTo(map.value)
}

watch(coordinates, () => {
  nextTick(() => makeMap())
})
watch(zoomLevel, () => {
  map.value.zoomTo(zoomLevel.value, { duration: 2000 })
})
watch(markerColor, () => {
  nextTick(() => makeMap())
})

onBeforeUnmount(() => {
  map.value.remove()
})
</script>

<style scoped>
@reference "../css/theme.css";

/* TODO Update package and change this!! */
:deep(.maplibregl-ctrl-zoom-in),
:deep(.maplibregl-ctrl-zoom-out) {
  @apply h-12 w-12;
}

@screen lg {
  :deep(.maplibregl-ctrl-zoom-in),
  :deep(.maplibregl-ctrl-zoom-out) {
    @apply h-8 w-8;
  }
}

:deep(.maplibregl-ctrl-zoom-in:focus),
:deep(.maplibregl-ctrl-zoom-out:focus) {
  @apply bg-gray-200;
}

:deep(.maplibregl-cooperative-gesture-screen) {
  @apply z-20;
}
</style>
